import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, pickImage } from "@/lib/api";
import { optimizeImageToWebp } from "@/lib/image";
import { useSite } from "@/context/SiteContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Header } from "./IndustriesPanel";
import BoxAnnotator from "./BoxAnnotator";
import { SITE_NAME } from "@/constants/site";

const SECTIONS = [
  {
    id: "brand",
    title: "Brand",
    fields: [
      { key: "brand_name", label: "Brand name", placeholder: "Bondstein" },
      { key: "product_name", label: "Product name", placeholder: SITE_NAME },
    ],
  },
  {
    id: "hero",
    title: "Hero",
    fields: [
      { key: "hero_eyebrow", label: "Eyebrow text", placeholder: "Bondstein · Smarter Spaces v1" },
      { key: "hero_title_lead", label: "Hero title - lead", placeholder: "See every space." },
      { key: "hero_title_highlight", label: "Hero title - highlighted phrase", placeholder: "Decide in real-time." },
      { key: "hero_subtitle", label: "Hero subtitle", multiline: true },
      { key: "cta_demo_label", label: "Primary CTA label", placeholder: "Request a demo" },
      { key: "cta_brochure_label", label: "Brochure CTA label", placeholder: "Download brochure" },
      { key: "brochure_url", label: "Brochure URL (public PDF)", placeholder: "/bondstein-smarter-spaces-brochure.pdf", hint: "Use a path on this site" },
    ],
  },
  {
    id: "trust",
    title: "Trust strip",
    fields: [
      { key: "trust_strip_eyebrow", label: "Eyebrow text" },
      { key: "trust_strip_logos", label: "Customer names / logos", list: true, hint: "One per line" },
    ],
  },
  {
    id: "lead",
    title: "Demo CTA section",
    fields: [
      { key: "lead_form_eyebrow", label: "Eyebrow text" },
      { key: "lead_form_title_lead", label: "Title - lead" },
      { key: "lead_form_title_highlight", label: "Title - highlighted phrase" },
      { key: "lead_form_subtitle", label: "Subtitle", multiline: true },
      { key: "lead_form_bullets", label: "Bullet list", list: true, hint: "One per line" },
    ],
  },
  {
    id: "contact",
    title: "Contact & footer",
    fields: [
      { key: "contact_email", label: "Email" },
      { key: "contact_phone", label: "Phone" },
      { key: "contact_address", label: "Address", multiline: true, hint: "Use new lines for multiple address lines" },
      { key: "footer_blurb", label: "Footer blurb", multiline: true },
    ],
  },
  {
    id: "pricing",
    title: "Pricing calculator",
    fields: [
      { key: "pricing_currency_symbol", label: "Currency symbol", placeholder: "৳" },
      { key: "pricing_currency_code", label: "Currency code", placeholder: "BDT" },
      { key: "pricing_server_set_size", label: "Cameras per server set", placeholder: "3" },
      { key: "pricing_server_monthly_per_set", label: "Server cost / set / month", placeholder: "30000" },
      { key: "pricing_camera_install_per_set", label: "Camera install cost / set (one-off)", placeholder: "30000" },
      { key: "pricing_feature_monthly", label: "Cost per feature / month", placeholder: "30000" },
      { key: "pricing_disclaimer", label: "Pricing disclaimer", multiline: true, hint: "Shown at the bottom of the pricing page" },
    ],
  },
];

export default function SiteSettingsPanel() {
  const site = useSite();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [optimizingLogo, setOptimizingLogo] = useState(false);
  const [optimizingHero, setOptimizingHero] = useState(false);

  useEffect(() => {
    setForm(site.settings || {});
    setLogoPreview(site.settings?.logo_url || site.settings?.logo_data || null);
  }, [site.settings]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleLogo = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Logo file too large (max 25 MB before optimization).");
      return;
    }
    // Keep SVG as-is so it stays vector-sharp.
    if (file.type === "image/svg+xml") {
      if (file.size > 1.5 * 1024 * 1024) {
        toast.error("SVG logo too large (max 1.5 MB).");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        update("logo_data", reader.result);
        update("logo_url", "");
        setLogoPreview(reader.result);
        toast.success("SVG logo attached.");
      };
      reader.onerror = () => toast.error("Could not read logo.");
      reader.readAsDataURL(file);
      return;
    }
    setOptimizingLogo(true);
    try {
      const webpDataUrl = await optimizeImageToWebp(file, {
        maxBytes: 1.5 * 1024 * 1024,
        quality: 0.95,
      });
      update("logo_data", webpDataUrl);
      update("logo_url", "");
      setLogoPreview(webpDataUrl);
      toast.success("Logo optimized to WebP.");
    } catch (e) {
      toast.error(e?.message || "Could not optimize logo.");
    } finally {
      setOptimizingLogo(false);
    }
  };

  const handleHeroImage = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Banner image too large (max 25 MB before optimization).");
      return;
    }
    setOptimizingHero(true);
    try {
      const webpDataUrl = await optimizeImageToWebp(file, {
        maxBytes: 2 * 1024 * 1024,
        quality: 0.95,
      });
      update("hero_snapshot_image_data", webpDataUrl);
      update("hero_snapshot_image_url", "");
      toast.success("Banner optimized to WebP. Drag on the image to draw boxes.");
    } catch (e) {
      toast.error(e?.message || "Could not optimize banner image.");
    } finally {
      setOptimizingHero(false);
    }
  };

  const clearLogo = () => {
    update("logo_data", null);
    update("logo_url", "");
    setLogoPreview(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Normalise list fields (already arrays in state)
      const payload = { ...form };
      await api.patch("/admin/settings", payload);
      toast.success("Site settings saved.");
      await site.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-panel" className="space-y-6 pb-20">
      <Header
        title="Site settings"
        subtitle="Logo and copy for the public landing page. Changes go live immediately after save."
        right={
          <Button
            type="button"
            data-testid="settings-save"
            disabled={saving}
            onClick={save}
            className="bg-[#84CC16] text-[#0A0A0A] hover:bg-[#65A30D] font-semibold"
          >
            {saving ? "Saving…" : "Save settings"}
          </Button>
        }
      />

      {/* Logo */}
      <section className="bg-white border border-neutral-200 rounded-xl p-5">
        <p className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-3">
          Logo
        </p>
        <div className="flex flex-wrap items-center gap-5">
          <div className="w-44 h-20 rounded-lg bg-neutral-50 border border-neutral-200 flex items-center justify-center overflow-hidden">
            {logoPreview ? (
              <img src={logoPreview} alt="Logo preview" className="max-h-16 max-w-40 object-contain" />
            ) : (
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-neutral-400">
                No logo
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label
              data-testid="settings-logo-upload-label"
              className={`text-xs font-semibold px-3 py-2 rounded-md bg-[#0A0A0A] text-white cursor-pointer hover:bg-neutral-800 ${
                optimizingLogo ? "opacity-60 pointer-events-none" : ""
              }`}
            >
              {optimizingLogo ? "Optimizing…" : "Upload logo (PNG/SVG)"}
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                disabled={optimizingLogo}
                data-testid="settings-logo-upload-input"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  handleLogo(f);
                }}
              />
            </label>
            {(form.logo_data || form.logo_url) && (
              <button
                type="button"
                data-testid="settings-logo-clear"
                onClick={clearLogo}
                className="text-xs font-semibold text-neutral-700 px-3 py-2 rounded-md border border-neutral-200"
              >
                Remove logo
              </button>
            )}
          </div>
        </div>
        <div className="mt-4">
          <Label>Or paste a hosted logo URL</Label>
          <Input
            data-testid="settings-input-logo-url"
            value={form.logo_url || ""}
            onChange={(e) => {
              update("logo_url", e.target.value);
              update("logo_data", null);
              setLogoPreview(e.target.value || null);
            }}
            placeholder="https://cdn.example.com/logo.svg"
          />
        </div>
      </section>

      {/* Hero snapshot - image + bounding boxes (same editor as feature cards) */}
      <section
        data-testid="settings-section-hero-snapshot"
        className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4"
      >
        <p className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500">
          Hero snapshot (banner on the homepage)
        </p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-3">
            <Label>Camera ID</Label>
            <Input
              data-testid="settings-input-hero_snapshot_cam_id"
              value={form.hero_snapshot_cam_id || ""}
              onChange={(e) => update("hero_snapshot_cam_id", e.target.value)}
              placeholder="CAM-04"
            />
          </div>
          <div className="md:col-span-5">
            <Label>Badge (bottom-left overlay)</Label>
            <Input
              data-testid="settings-input-hero_snapshot_badge"
              value={form.hero_snapshot_badge || ""}
              onChange={(e) => update("hero_snapshot_badge", e.target.value)}
              placeholder="OBJ · 5 · ALERT · 1"
            />
          </div>
          <div className="md:col-span-4 grid grid-cols-3 gap-2">
            <div>
              <Label>Persons</Label>
              <Input data-testid="settings-input-hero_stat_persons" value={form.hero_stat_persons || ""} onChange={(e) => update("hero_stat_persons", e.target.value)} placeholder="05" />
            </div>
            <div>
              <Label>Compliance</Label>
              <Input data-testid="settings-input-hero_stat_compliance" value={form.hero_stat_compliance || ""} onChange={(e) => update("hero_stat_compliance", e.target.value)} placeholder="80%" />
            </div>
            <div>
              <Label>Alerts</Label>
              <Input data-testid="settings-input-hero_stat_alerts" value={form.hero_stat_alerts || ""} onChange={(e) => update("hero_stat_alerts", e.target.value)} placeholder="01" />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <label
            data-testid="settings-hero-upload-label"
            className={`text-xs font-semibold px-3 py-2 rounded-md bg-[#0A0A0A] text-white cursor-pointer hover:bg-neutral-800 ${
              optimizingHero ? "opacity-60 pointer-events-none" : ""
            }`}
          >
            {optimizingHero ? "Optimizing…" : "Upload banner image"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,image/bmp"
              className="hidden"
              disabled={optimizingHero}
              data-testid="settings-hero-upload-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                handleHeroImage(f);
              }}
            />
          </label>
          {(form.hero_snapshot_image_data || form.hero_snapshot_image_url) && (
            <button
              type="button"
              data-testid="settings-hero-image-clear"
              onClick={() => {
                update("hero_snapshot_image_data", null);
                update("hero_snapshot_image_url", "");
              }}
              className="text-xs font-semibold text-neutral-700 px-3 py-2 rounded-md border border-neutral-200"
            >
              Remove banner image
            </button>
          )}
          <div className="flex-1 min-w-[260px]">
            <Input
              data-testid="settings-input-hero_snapshot_image_url"
              value={form.hero_snapshot_image_url || ""}
              onChange={(e) => {
                update("hero_snapshot_image_url", e.target.value);
                update("hero_snapshot_image_data", null);
              }}
              placeholder="…or paste a hosted image URL (https://…)"
              disabled={Boolean(form.hero_snapshot_image_data)}
            />
          </div>
        </div>

        <BoxAnnotator
          src={pickImage({ image_data: form.hero_snapshot_image_data, image_url: form.hero_snapshot_image_url })}
          boxes={form.hero_snapshot_boxes || []}
          onChange={(b) => update("hero_snapshot_boxes", b)}
          camId={form.hero_snapshot_cam_id || "CAM-04"}
        />

        <p className="text-xs text-neutral-500">
          Boxes drawn here render with the same engine as the feature cards. Click &amp; drag on
          the image to add a box, click any box to edit colour / label / blink / border / confidence.
        </p>
      </section>

      {SECTIONS.map((section) => (
        <section
          key={section.id}
          data-testid={`settings-section-${section.id}`}
          className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4"
        >
          <p className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500">
            {section.title}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {section.fields.map((f) => (
              <div key={f.key} className={f.multiline || f.list ? "md:col-span-2" : ""}>
                <Label>{f.label}{f.hint && <span className="text-neutral-400 ml-1 normal-case tracking-normal">- {f.hint}</span>}</Label>
                {f.list ? (
                  <Textarea
                    data-testid={`settings-input-${f.key}`}
                    value={(form[f.key] || []).join("\n")}
                    onChange={(e) =>
                      update(
                        f.key,
                        e.target.value
                          .split("\n")
                          .map((s) => s.trim())
                          .filter(Boolean)
                      )
                    }
                    rows={5}
                  />
                ) : f.multiline ? (
                  <Textarea
                    data-testid={`settings-input-${f.key}`}
                    value={form[f.key] || ""}
                    onChange={(e) => update(f.key, e.target.value)}
                    rows={3}
                  />
                ) : (
                  <Input
                    data-testid={`settings-input-${f.key}`}
                    value={form[f.key] || ""}
                    onChange={(e) => update(f.key, e.target.value)}
                    placeholder={f.placeholder}
                  />
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

const Label = ({ children }) => (
  <span className="block font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1.5">
    {children}
  </span>
);
